/**
 * Canonical observ-record.sh template distribution for aaac init / update.
 *
 * The shell recorder is materialized to the path declared in
 * project.yaml `bindings.observability.recorder` when it names the managed
 * observ-record wrapper (ends with `observ-record.sh`).
 */

import { existsSync } from "node:fs";
import { chmod, mkdir, readFile, unlink, writeFile } from "node:fs/promises";
import { dirname, join, resolve } from "node:path";
import { fileURLToPath } from "node:url";

export const DEFAULT_RECORDER_REL = ".cursor/hooks/observ-record.sh";

/** True when governance should ship/sync the bundled observ-record.sh template. */
export function isManagedRecorderPath(recorder: string): boolean {
  const normalized = recorder.replace(/^\.\//, "");
  return normalized.endsWith("observ-record.sh");
}

function resolveRecorderTemplatePath(): string {
  const here = dirname(fileURLToPath(import.meta.url));
  return join(here, "../../templates/observ-record.sh");
}

/** Read the bundled observ-record.sh template (from package templates/). */
export async function readRecorderScriptTemplate(): Promise<string> {
  return readFile(resolveRecorderTemplatePath(), "utf8");
}

export interface MaterializeRecorderScriptResult {
  path: string;
  action: "created" | "updated" | "unchanged";
}

/**
 * Write the canonical observ-record.sh when content differs (idempotent).
 * Sets mode 0755 on create/update.
 */
export async function materializeRecorderScript(
  projectRoot: string,
  recorderRelPath: string,
): Promise<MaterializeRecorderScriptResult> {
  const root = resolve(projectRoot);
  const relPath = recorderRelPath.replace(/^\.\//, "");
  const absPath = join(root, relPath);
  const template = await readRecorderScriptTemplate();

  const existed = existsSync(absPath);
  const existing = existed ? await readFile(absPath, "utf8") : undefined;

  if (existing === template) {
    return { path: relPath, action: "unchanged" };
  }

  await mkdir(dirname(absPath), { recursive: true });
  await writeFile(absPath, template, "utf8");
  await chmod(absPath, 0o755);

  return { path: relPath, action: existed ? "updated" : "created" };
}

/** Remove a materialized recorder script (aaac uninstall). */
export async function unmaterializeRecorderScript(
  projectRoot: string,
  recorderRelPath: string,
): Promise<void> {
  const absPath = join(resolve(projectRoot), recorderRelPath.replace(/^\.\//, ""));
  if (existsSync(absPath)) {
    await unlink(absPath);
  }
}
